// /privacy — minimal privacy policy for the Phase A landing ship.
//
// Surface scope: only the waitlist email + the locale chosen when it
// was left. Resend acts as the only sub-processor. No analytics, no
// cookies beyond i18next-browser-languagedetector's own (which lives
// in localStorage, not a cookie). When Phase B (full app) lands, this
// document expands to cover bond data, letters, memories, and the
// rest. Until then, honesty about the small surface area beats a
// boilerplate policy that overstates what is collected.
//
// Not legal-counsel-reviewed; private beta. EU jurisdiction (Madrid).

import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { BrandMark } from '../components/BrandMark';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

function PrivacyPage() {
  const { t } = useTranslation();
  const sections = ['what', 'visits', 'where', 'how', 'leave'] as const;

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-2xl flex-col items-center pt-12 text-center animate-fadeUp sm:pt-8 lg:pt-4">
      <BrandMark />
      <p className="mt-10 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.5em] sm:text-xs">
        {t('privacy.eyebrow')}
      </p>
      <h1 className="mt-5 max-w-2xl font-display font-light text-ash-100 text-3xl leading-snug sm:text-4xl">
        {t('privacy.heading')}
      </h1>
      <p className="mt-6 max-w-xl font-body text-ash-300 italic leading-relaxed sm:text-lg">
        {t('privacy.intro')}
      </p>

      <div className="mt-12 flex w-full max-w-xl flex-col gap-8 text-left">
        {sections.map((key) => (
          <section key={key}>
            <h2 className="font-display text-[0.7rem] text-ash-400 uppercase tracking-[0.4em] sm:text-xs">
              {t(`privacy.sections.${key}.title`)}
            </h2>
            <p className="mt-3 font-body text-ash-200 leading-relaxed sm:text-[1.05rem]">
              {t(`privacy.sections.${key}.body`)}
            </p>
          </section>
        ))}
      </div>

      <div className="hairline mt-16 h-px w-32" />

      <p className="mt-8 font-body text-ash-500 text-xs italic">
        {t('privacy.contact')}
      </p>

      <Link
        to="/"
        className="mt-10 font-display text-[0.7rem] text-ash-400 uppercase tracking-[0.4em] transition hover:text-ash-200 sm:text-xs"
      >
        ← {t('privacy.backToLanding')}
      </Link>
    </div>
  );
}
